import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  InputBase,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../routes';

const Home: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          pt: 12,
          pb: 16,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 4 }}
          >
            Find Your Perfect Tutor Today
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{ mb: 6, opacity: 0.9, maxWidth: '800px', mx: 'auto' }}
          >
            Connect with expert tutors for any subject. We believe in fair prices for students and fair pay for tutors.
          </Typography>

          {/* Search Bar */}
          <Paper
            component="form"
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 600,
              mx: 'auto',
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <InputBase
              sx={{ ml: 2, flex: 1, fontSize: '1.1rem' }}
              placeholder="What do you want to learn?"
              inputProps={{ 'aria-label': 'search tutors' }}
            />
            <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
              <SearchIcon color="primary" />
            </IconButton>
            <Button
              variant="contained"
              sx={{ m: 0.5, px: 4, borderRadius: 1.5 }}
              disableElevation
            >
              Search
            </Button>
          </Paper>

          {/* Waitlist Banner */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              We are currently in beta. <a href="/waitlist" style={{ color: 'white', fontWeight: 'bold', textDecoration: 'underline' }}>Join our waitlist</a> to get early access!
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Value Proposition Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <SecurityIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                Transparent & Fair
              </Typography>
              <Typography variant="body1" color="text.secondary">
                We charge significantly lower fees than other platforms, ensuring your money goes directly to high-quality education.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <GroupIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                Expert Tutors
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Our tutors are vetted professionals and passionate educators dedicated to helping you succeed.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <TrendingUpIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                Results Driven
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Track your progress and achieve your academic goals with personalized learning plans.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Become a Tutor Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                Share your knowledge, earn on your terms.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', mb: 4 }}>
                Join a community of educators who are valued and respected. Set your own rates, manage your schedule, and keep more of what you earn.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => navigate(AppRoutes.BECOME_TUTOR)}
                sx={{ px: 4, py: 1.5, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
              >
                Become a Tutor
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  height: 300,
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  borderRadius: 4,
                }}
              >
                <Typography variant="h5">Tutor Image / Illustration</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
